import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Enable timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Format UTC datetime string to user's timezone
 * @param utcTimeString - UTC datetime string from backend
 * @param userTimezone - User's timezone (default: Asia/Kolkata for IST)
 * @param format - Display format
 * @returns Formatted time string in user's timezone
 */
export const formatTimeInUserTimezone = (
  utcTimeString: string, 
  userTimezone: string = 'Asia/Kolkata',
  format: string = 'MMM D, YYYY h:mm A'
): string => {
  if (!utcTimeString) return '-';
  
  try {
    // Parse as UTC and convert to user timezone
    return dayjs.utc(utcTimeString).tz(userTimezone).format(format);
  } catch (error) {
    console.error('Error formatting time:', error);
    return utcTimeString; // Fallback to original string
  }
};

/**
 * Format UTC datetime for display with timezone indicator
 * @param utcTimeString - UTC datetime string from backend
 * @param userTimezone - User's timezone
 * @returns Formatted time with timezone abbreviation
 */
export const formatTimeWithTimezone = (
  utcTimeString: string,
  userTimezone: string = 'Asia/Kolkata'
): string => {
  if (!utcTimeString) return '-';
  
  try {
    const formatted = dayjs.utc(utcTimeString).tz(userTimezone);
    return `${formatted.format('MMM D, YYYY h:mm A')} ${formatted.format('z')}`;
  } catch (error) {
    console.error('Error formatting time with timezone:', error);
    return utcTimeString;
  }
};

/**
 * Get current time in user's timezone
 * @param userTimezone - User's timezone
 * @returns Current time in user's timezone
 */
export const getCurrentTimeInTimezone = (userTimezone: string = 'Asia/Kolkata'): dayjs.Dayjs => {
  return dayjs().tz(userTimezone);
};

/**
 * Convert local time to UTC for API calls
 * @param localTime - Local time (dayjs object or string)
 * @param userTimezone - User's timezone
 * @returns UTC time string for API
 */
export const convertToUTCForAPI = (
  localTime: dayjs.Dayjs | string,
  userTimezone: string = 'Asia/Kolkata'
): string => {
  try {
    if (typeof localTime === 'string') {
      // If it's a string, parse it in user's timezone
      return dayjs.tz(localTime, userTimezone).utc().toISOString();
    } else {
      // If it's a dayjs object, ensure it's in user's timezone then convert to UTC
      return localTime.tz(userTimezone).utc().toISOString();
    }
  } catch (error) {
    console.error('Error converting to UTC:', error);
    return dayjs().utc().toISOString(); // Fallback to current UTC
  }
};

/**
 * Check if a time is in the past relative to user's timezone
 * @param utcTimeString - UTC datetime string
 * @param userTimezone - User's timezone
 * @returns true if the time is in the past
 */
export const isTimeInPast = (
  utcTimeString: string,
  userTimezone: string = 'Asia/Kolkata'
): boolean => {
  if (!utcTimeString) return false;
  
  try {
    const scheduledTime = dayjs.utc(utcTimeString).tz(userTimezone);
    const currentTime = dayjs().tz(userTimezone);
    return scheduledTime.isBefore(currentTime);
  } catch (error) {
    console.error('Error checking if time is in past:', error);
    return false;
  }
};

/**
 * Calculate time remaining until a scheduled time
 * @param utcTimeString - UTC datetime string
 * @param userTimezone - User's timezone
 * @returns Object with remaining time info
 */
export const calculateTimeRemaining = (
  utcTimeString: string,
  userTimezone: string = 'Asia/Kolkata'
) => {
  if (!utcTimeString) {
    return { formatted: 'Not scheduled', isOverdue: true, totalSeconds: 0 };
  }
  
  try {
    const scheduledTime = dayjs.utc(utcTimeString);
    const currentTime = dayjs.utc();
    const diff = scheduledTime.diff(currentTime);
    
    if (diff <= 0) {
      return { formatted: 'Overdue', isOverdue: true, totalSeconds: 0 };
    }
    
    const duration = dayjs.duration(diff);
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    
    let formatted = '';
    if (days > 0) formatted += `${days}d `;
    if (hours > 0) formatted += `${hours}h `;
    if (minutes > 0) formatted += `${minutes}m `;
    if (seconds > 0 && days === 0) formatted += `${seconds}s`;
    
    return {
      formatted: formatted.trim() || 'Less than 1s',
      isOverdue: false,
      totalSeconds: Math.floor(diff / 1000)
    };
  } catch (error) {
    console.error('Error calculating time remaining:', error);
    return { formatted: 'Error', isOverdue: true, totalSeconds: 0 };
  }
}; 